// src/features/calendar/settings/WorkingHoursSettings.tsx
// "Say when I am workable at all" — the current user's weekly availability.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useCalendarAvailability, useI18n } from '@react/hooks';
import { authStore } from '@core/stores';
import { Button, Input, Alert } from '@react/components';
import { useScope } from '@/hooks/useScope';
import type { WorkingPeriod } from '@core/types/calendar';
import { WeeklyPeriodsEditor } from '../WeeklyPeriodsEditor';
import { errMessage, makeEntity } from '../time';

export const WorkingHoursSettings = observer(() => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useCalendarAvailability();

  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone ?? '');
  const [periods, setPeriods] = useState<WorkingPeriod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const actorId = authStore.activeProfileId;
    if (!actorId) {
      setIsLoading(false);
      return;
    }
    store
      .GetWorkingHours(actorId)
      .then((res) => {
        if (res.working_hours) {
          setTimezone(res.working_hours.timezone || timezone);
          setPeriods(res.working_hours.periods ?? []);
        }
      })
      .catch(() => {
        // No working hours configured yet — start empty.
      })
      .finally(() => setIsLoading(false));
  }, []);

  const save = async () => {
    setIsSaving(true);
    setError(null);
    setSaved(false);
    try {
      await store.SetWorkingHours({
        scope: makeEntity(scope.productId, 'product', scope),
        timezone,
        periods,
      });
      setSaved(true);
    } catch (e) {
      setError(errMessage(e, t('calendar.workingHours.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <div className="h-8 w-1/3 animate-pulse rounded bg-muted" />
        <div className="h-48 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      <p className="text-sm text-muted-foreground">{t('calendar.workingHours.hint')}</p>
      {error && <Alert variant="error">{error}</Alert>}
      {saved && <Alert variant="success">{t('calendar.workingHours.saved')}</Alert>}
      <Input
        label={t('calendar.event.timezone')}
        value={timezone}
        onChange={(e) => setTimezone(e.target.value)}
        placeholder="Europe/Amsterdam"
      />
      <WeeklyPeriodsEditor periods={periods} onChange={setPeriods} />
      <Button onClick={save} isLoading={isSaving}>
        {t('common.save')}
      </Button>
    </div>
  );
});
